import React from 'react';
import { Box, Card, CardActionArea, Typography, Chip } from '@mui/material';
import { useTheme } from '@mui/system';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';

const RankHeaderCard = ({
    color,
    title,
    onClick,
    selected,
    isChecked,
}: {
    color: string;
    title: string;
    onClick: () => void;
    selected: boolean;
    isChecked: boolean;
}) => {
    const theme = useTheme();
    return (
        <Card
            elevation={0}
            sx={{
                backgroundColor: 'transparent',
                border: selected ? `2px solid ${color}` : `1px solid rgba(255,255,255,0.1)`,
                borderRadius: 4,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                overflow: 'visible',
                position: 'relative',
                backdropFilter: 'blur(10px)',
            }}
        >
            <CardActionArea
                onClick={onClick}
                sx={{
                    backgroundColor: 'transparent',
                    '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.02)',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 140,
                        p: 3,
                        position: 'relative',
                    }}
                >
                    {/* Circular Medal */}
                    <Box
                        sx={{
                            width: 100,
                            height: 100,
                            borderRadius: '50%',
                            background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.4), ${color} 40%, ${color}DD 100%)`,
                            border: `4px solid ${color}`,
                            boxShadow: `
                                inset 0 0 20px rgba(255,255,255,0.2),
                                inset 0 0 40px ${color}40,
                                0 4px 20px rgba(0,0,0,0.3),
                                0 0 30px ${color}60
                            `,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            mb: 2,
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                width: '80%',
                                height: '80%',
                                borderRadius: '50%',
                                border: `2px solid rgba(255,255,255,0.3)`,
                                boxShadow: `inset 0 0 10px rgba(0,0,0,0.2)`,
                            },
                        }}
                    >
                        {/* Central Star */}
                        <StarIcon
                            sx={{
                                fontSize: 50,
                                color: 'white',
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))',
                                zIndex: 1,
                            }}
                        />
                        {isChecked && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    backgroundColor: theme.palette.success.main,
                                    borderRadius: '50%',
                                    width: 32,
                                    height: 32,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: `0 4px 12px ${theme.palette.success.main}60`,
                                    border: '2px solid white',
                                    zIndex: 2,
                                }}
                            >
                                <CheckCircleIcon sx={{ fontSize: 20, color: 'white' }} />
                            </Box>
                        )}
                    </Box>
                    {/* 3D Metallic Text */}
                    <Typography
                        sx={{
                            fontWeight: 900,
                            fontSize: 20,
                            textTransform: 'uppercase',
                            letterSpacing: '2px',
                            background: `linear-gradient(135deg, ${color} 0%, ${color}CC 50%, ${color} 100%)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            textShadow: `
                                2px 2px 0px rgba(0,0,0,0.3),
                                0px 0px 20px ${color}80
                            `,
                            filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
                            mb: isChecked ? 1 : 0,
                        }}
                    >
                        {title}
                    </Typography>
                    {isChecked && (
                        <Chip
                            icon={<StarIcon sx={{ fontSize: 14 }} />}
                            label="Current"
                            size="small"
                            sx={{
                                mt: 1,
                                backgroundColor: theme.palette.success.main,
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.65rem',
                                height: 22,
                                boxShadow: `0 2px 8px ${theme.palette.success.main}60`,
                                border: '1px solid rgba(255,255,255,0.3)',
                            }}
                        />
                    )}
                </Box>
            </CardActionArea>
        </Card>
    );
};

export default RankHeaderCard;
